import { applySnapshot, cast, detach, flow, Instance, SnapshotIn, toGenerator, types } from 'mobx-state-tree'

import { FetchModel } from '../models/FetchModel'
import {
  MissionHistoryExtraDataModel,
  MissionHistoryModel,
  MissionHistoryReportModel,
  MissionHistoryReportSnapshot,
  MissionLogType,
} from '../models/MissionsHistory/MissionHistoryModel'
import { FilterModel } from '../models/common/Filters/FilterModel'
import { FilterType } from '../models/common/Filters/FilterType'
import { SelectFieldFilter, SelectFilterIcon } from '../models/common/Filters/SelectFieldFilter'
import {
  OperationFailure,
  OperationInProgress,
  OperationNotStarted,
  OperationStatus,
  OperationStatusModel,
  OperationSuccess,
} from '../models/common/OperationStatus'
import { downloadFile } from '../utils/fileUtils'
import { HttpAttachment } from '../utils/httpAttachment'

import type { MissionHistoryFilterValues } from '../models/common/Filters/FiltersValueModels'
import { IsEditingModel } from '../models/IsEditingModel'

const MISSION_HISTORY_FILTERS: SnapshotIn<typeof FilterModel> = {
  fieldFilters: {
    mission_id: { type: FilterType.SEARCH, placeholder: 'Mission ID', fieldNetworkName: 'mission_id' },
    user: { type: FilterType.SELECT, icon: SelectFilterIcon.User, fieldNetworkName: 'rpic_user_id' },
    vehicle: { type: FilterType.SELECT, icon: SelectFilterIcon.Vehicle, fieldNetworkName: 'vehicle_id' },
    date: { type: FilterType.DATE },
  },
}

const MISSION_REPORT_PAGE_SIZE = 10

export const MissionNoteEditingCloneModel= IsEditingModel.named('MissionNoteEditingCloneModel')
  .props({
    mission_note: types.optional(types.string, '')
  })
  .actions((self) => ({
    setMissionNote(note: string) {
      self.mission_note = note
    }
  }))

export const MissionHistoryStore = FetchModel.named('MissionHistoryStore')
  .props({
    missionHistoryReport: types.maybeNull(MissionHistoryReportModel),
    fetchReportStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),
    fetchNextReportPageStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    selectedMission: types.safeReference(MissionHistoryModel),

    lastFetchedReportPage: types.maybeNull(types.number),

    missionExtraData: types.map(MissionHistoryExtraDataModel),

    filters: types.optional(FilterModel, MISSION_HISTORY_FILTERS),
    fetchFilterOptionsStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    downloadPayloadReportStatus: types.optional(OperationStatusModel, () => OperationNotStarted()),

    downloadLogStatuses: types.map(OperationStatusModel),
    fetchMissionExtraDataStatuses: types.map(OperationStatusModel),
    requestLogUploadStatuses: types.map(OperationStatusModel),
    downloadTelemetryStatuses: types.map(OperationStatusModel),

    missionNoteForEditing: types.maybeNull(MissionNoteEditingCloneModel),
    isMissionNotePanelOpen: types.optional(types.boolean, false),
    saveMissionNoteStatuses: types.map(OperationStatusModel),
  })
  .views((self) => ({
    _missionHistoryReportUrl(pageToFetch: number): string {
      return `${self.baseUrl}history/?${self.filters.asUrlSearchParams}&page_size=${MISSION_REPORT_PAGE_SIZE}&page_number=${pageToFetch}`
    },
    get _missionHistoryCsvUrl(): string {
      return `${self.baseUrl}history/csv_dump/?${self.filters.asUrlSearchParams}`
    },
    get _payloadReportUrl(): string {
      return `${self.baseUrl}history/payload_report/?${self.filters.asUrlSearchParams}`
    },
    _missionHistoryExtendedDataUrl(missionId: string): string {
      return `${self.baseUrl}history/${missionId}/`
    },
    get _missionHistoryFilterValuesUrl(): string {
      return `${self.baseUrl}history/filter/`
    },
    get _requestLogUploadUrl(): string {
      return `${self.baseUrl}history/logs/request_log_upload/`
    },
    saveMissionNoteUrl(missionId: string): string {
      return `${self.baseUrl}mission_notes/${missionId}/`
    }
  }))
  .views((self) => ({
    _getFilterByFieldName(fieldName: string) {
      const filter = self.filters.fieldFilters.get(fieldName)
      if (filter === undefined) {
        throw new Error(`filter ${fieldName} must appear`)
      }
      return filter
    },
  }))
  .views((self) => ({
    get _userFilter(): SelectFieldFilter {
      return self._getFilterByFieldName('user') as SelectFieldFilter
    },
    get _vehicleFilter(): SelectFieldFilter {
      return self._getFilterByFieldName('vehicle') as SelectFieldFilter
    },
  }))
  .actions((self) => {
    const _fetchMissionData = flow(function* _fetchMissionExtraData<T>(missionId: string) {
      return yield* toGenerator(
        self.loadObjectOrError<T>(
          self._missionHistoryExtendedDataUrl(missionId),
          {},
          { fetchOperationName: 'Fetch detailed mission history' },
        ))
    })
    const _fetchMissionExtraData = (missionId: string) => _fetchMissionData<SnapshotIn<typeof MissionHistoryExtraDataModel>>(missionId)
    const _fetchMission = (missionId: string) => _fetchMissionData<SnapshotIn<typeof MissionHistoryModel>>(missionId)
    return {
      _fetchMissionExtraData,
      _fetchMission
    }})
  .actions((self) => ({
    _updateOperationStatus(operationStatusMap: typeof self.requestLogUploadStatuses, key: string, newStatus: OperationStatus) {
      const oldStatus = operationStatusMap.get(key) 
      if (oldStatus !== undefined) {
        detach(oldStatus)
      }
      operationStatusMap.set(key, newStatus)
    }
  }))
  .actions((self) => ({
    fetchAndSetMissionExtraData: flow(function* fetchMissionExtraData(missionId: string) {
        self._updateOperationStatus(self.fetchMissionExtraDataStatuses, missionId, OperationInProgress())

        const loadResult = yield* toGenerator(self._fetchMissionExtraData(missionId))

        if (loadResult.error) {
          self._updateOperationStatus(self.fetchMissionExtraDataStatuses, missionId, OperationFailure(loadResult.error))
          return
        }

        if (!MissionHistoryExtraDataModel.is(loadResult.result)) {
          console.error('Failed converting server response to client schema')
          self._updateOperationStatus(self.fetchMissionExtraDataStatuses, missionId, OperationFailure({
            name: 'SchemaCompatibilityError',
            message: 'Server response not compatible with client schema',
          }))
          return
        }

        const missionExtraData = MissionHistoryExtraDataModel.create(loadResult.result)
        self.missionExtraData.put(missionExtraData)
        
        self._updateOperationStatus(self.fetchMissionExtraDataStatuses, missionId, OperationSuccess())
    }),
    fetchAndSetFilterValues: flow(function* fetchAndSetFilterValues() {
      self.fetchFilterOptionsStatus = OperationInProgress()

      const loadResult = yield* toGenerator(
        self.loadObjectOrError<MissionHistoryFilterValues>(
          self._missionHistoryFilterValuesUrl,
          {},
          { fetchOperationName: 'Fetch mission history filter values' },
        ),
      )
      if (loadResult.error) {
        self.fetchFilterOptionsStatus = OperationFailure(loadResult.error)
        return
      }
      self._userFilter.setOptions(
        loadResult.result.rpics.map((user) => ({
          value: user.rpic_user_id.toString(),
          text: user.rpic_user_name,
        })),
      )
      self._vehicleFilter.setOptions(
        loadResult.result.vehicles.map((vehicle) => ({
          value: vehicle.vehicle_id.toString(),
          text: vehicle.vehicle_name,
        })),
      )

      self.fetchFilterOptionsStatus = OperationSuccess()
    }),
  }))
  .actions((self) => ({
    fetchAndSetMissionHistoryReport: flow(function* fetchAndSetMissionHistoryReport() {
      self.fetchReportStatus = OperationInProgress()

      self.lastFetchedReportPage = null
      const pageToFetch = 1
      const loadResult = yield* toGenerator(
        self.loadObjectOrError<MissionHistoryReportSnapshot>(
          self._missionHistoryReportUrl(pageToFetch),
          {},
          { fetchOperationName: 'Fetch mission history report' },
        ),
      )

      if (loadResult.error) {
        self.fetchReportStatus = OperationFailure(loadResult.error)
        return
      }

      if (!MissionHistoryReportModel.is(loadResult.result)) {
        console.error('Failed converting server response to client schema')
        self.fetchReportStatus = OperationFailure({
          name: 'SchemaCompatibilityError',
          message: 'Server response not compatible with client schema',
        })
        return
      }

      self.selectedMission = undefined
      self.missionNoteForEditing = null
      self.missionHistoryReport = MissionHistoryReportModel.create(loadResult.result)
      self.lastFetchedReportPage = 1
      self.fetchReportStatus = OperationSuccess()
    }),
    fetchNextReportPage: flow(function* fetchNextReportPage() {
      if (self.lastFetchedReportPage === null || self.missionHistoryReport === null) {
        throw new Error("Can't load next page if first page not fetched!")
      }
      self.fetchNextReportPageStatus = OperationInProgress()

      const loadResult = yield* toGenerator(
        self.loadObjectOrError<MissionHistoryReportSnapshot>(
          self._missionHistoryReportUrl(self.lastFetchedReportPage + 1),
          {},
          { fetchOperationName: 'Fetch next mission history report page' },
        ),
      )

      if (loadResult.error) {
        self.fetchNextReportPageStatus = OperationFailure(loadResult.error)
        return
      }

      if (!MissionHistoryReportModel.is(loadResult.result)) {
        console.error('Failed converting server response to client schema')
        self.fetchNextReportPageStatus = OperationFailure({
          name: 'SchemaCompatibilityError',
          message: 'Server response not compatible with client schema',
        })
        return
      }

      if (self.missionHistoryReport.metadata.totalResults !== loadResult.result.metadata.total_results) {
        // The report could have changed even if the number of results didn't, but we don't have a way to tell for we ignore this issue for now.
        self.fetchNextReportPageStatus = OperationFailure({
          name: 'ReportChangedError',
          message: 'The report was changed since loading, please refresh the page',
        })
        return
      }
      const newMissionsSnapshot = loadResult.result.missions ?? []
      const newMissions = newMissionsSnapshot.map((mission) => {
        return MissionHistoryModel.create(mission)
      })
      self.missionHistoryReport.missions.push(...newMissions)
      self.missionHistoryReport.metadata = cast(loadResult.result.metadata)
      self.lastFetchedReportPage = self.lastFetchedReportPage + 1
      self.fetchNextReportPageStatus = OperationSuccess()
    }),
    setSelectedMission(mission: Instance<typeof MissionHistoryModel>): void {
      self.selectedMission = mission
      self.missionNoteForEditing = MissionNoteEditingCloneModel.create({ mission_note: self.selectedMission.mission_note ?? '' })
      if (!self.missionExtraData.has(self.selectedMission.id)) {
        void self.fetchAndSetMissionExtraData(mission.id)
      }
    },
    requestUploadLog: flow(function* requestUploadLog(missionId: string, logType: MissionLogType) {

      const logKey = `${missionId}-${logType}`
      self._updateOperationStatus(self.requestLogUploadStatuses,logKey, OperationInProgress())

      const uploadLogRequestBody = JSON.stringify({ mission_id: missionId, log_type: logType})
      const uploadLogRequestResult = yield* toGenerator(self.loadObjectOrError<void>(
        self._requestLogUploadUrl,
          { method: 'POST', body: uploadLogRequestBody},
          { fetchOperationName: `request ${logType} log for mission ${missionId}` },
      ))

      if (uploadLogRequestResult.error) {
        self._updateOperationStatus(self.requestLogUploadStatuses,logKey, OperationFailure(uploadLogRequestResult.error))
        return
      }
      self._updateOperationStatus(self.requestLogUploadStatuses,logKey, OperationSuccess())
    })
  }))
  .views((self) => ({
    get canDownloadPayloadReport() {
      return (
        self.fetchReportStatus.isSuccess &&
        self._vehicleFilter.filterValue?.value !== undefined &&
        !self.downloadPayloadReportStatus.inProgress
      )
    },
    get downloadPayloadReportDisabledReason(): string | null {
      if (!self.fetchReportStatus.isSuccess) return "Can't download payload report before a report is returned"
      if (self._vehicleFilter.filterValue?.value === undefined) return 'Please select a vehicle first'
      if (self.downloadPayloadReportStatus.inProgress) return 'Download already in progress'
      return null
    },
  }))
  .actions((self) => ({
    downloadReportAsCsv() {
      const csvReportUrl = self._missionHistoryCsvUrl
      downloadFile(csvReportUrl)
    },
    downloadPayloadReport: flow(function* downloadPayloadReport() {
      const vehicleId = self._vehicleFilter.filterValue?.value
      if (vehicleId === undefined) {
        throw new Error("Can't download payload report if vehicle not selected")
      }
      self.downloadPayloadReportStatus = OperationInProgress()

      const fetchResult = yield* toGenerator(self.fetch(self._payloadReportUrl))

      if (!fetchResult.response) {
        self.downloadPayloadReportStatus = OperationFailure(
          fetchResult.error || { message: 'Failed to download Payload Report', name: '' },
        )
        return
      }

      const payloadReport = yield* toGenerator(HttpAttachment.fromResponse(fetchResult.response))
      payloadReport.download()

      self.downloadPayloadReportStatus = OperationSuccess()
    }),
    downloadTelemetryFile: flow(function* downloadTelemetryFile(missionId: string) {
      self._updateOperationStatus(self.downloadTelemetryStatuses, missionId, OperationInProgress())

      if (!self.missionExtraData.has(missionId)) {
        yield self.fetchAndSetMissionExtraData(missionId)
      }
      const missionExtraData = self.missionExtraData.get(missionId)
      if (missionExtraData === undefined) {
        self._updateOperationStatus(self.downloadTelemetryStatuses, missionId, OperationFailure({ name: '', message: 'Failed to fetch mission extra data' }))
        return
      }
      if (missionExtraData.telemetry_file_url === null || missionExtraData.telemetry_file_url === undefined) {
        self._updateOperationStatus(self.downloadTelemetryStatuses, missionId, OperationFailure({
          name: '',
          message: 'No telemetry file was found for this mission',
        }))
      } else {
        downloadFile(missionExtraData.telemetry_file_url)
        self._updateOperationStatus(self.downloadTelemetryStatuses, missionId, OperationSuccess())
      }
    }),
    requestOrDownloadMissionLog: flow(function* requestOrDownloadLog(missionId: string, logType: MissionLogType) {
      const logKey = `${missionId}-${logType}`
      self._updateOperationStatus(self.downloadLogStatuses, logKey, OperationInProgress())

      if (!self.missionExtraData.has(missionId)) {
        yield self.fetchAndSetMissionExtraData(missionId)
      }
      
      const missionExtraData = self.missionExtraData.get(missionId)
      if (missionExtraData === undefined) {
        self._updateOperationStatus(self.downloadLogStatuses, logKey, OperationFailure({ name: '', message: 'Failed to fetch mission extra data' }))

        return
      }
       
      const logFileInfo = missionExtraData.logFile(logType) 
      if (logFileInfo?.log_file_url) {
        // We have the URL, download the file
        downloadFile(logFileInfo.log_file_url)
        self._updateOperationStatus(self.downloadLogStatuses, logKey, OperationSuccess())
        return
      } else {
        // No URL, request to upload
        yield self.requestUploadLog(missionId, logType)
        // Todo: set timeout/interval to check whether file has uploaded already
    }      
    }),
    saveMissionNoteForSelectedMission: flow(function* saveMissionNoteForSelectedMission() {
      if (self.selectedMission === undefined || self.missionNoteForEditing === null)  {
        throw new Error('No mission selected, cannot save note')
      }

      self._updateOperationStatus(self.saveMissionNoteStatuses, self.selectedMission.id, OperationInProgress())

      const requestBody = JSON.stringify({ 'mission_note': self.missionNoteForEditing.mission_note })
      const saveNoteResult = yield* toGenerator(self.loadObjectOrError<void>(
        self.saveMissionNoteUrl(self.selectedMission.id),
        { method: 'PATCH', body: requestBody },
        { fetchOperationName: 'Save mission note '}
      ))

      if (saveNoteResult.error) {
        self._updateOperationStatus(self.saveMissionNoteStatuses, self.selectedMission.id, OperationFailure(saveNoteResult.error))
        return
      }

      // Reload this mission and update the selected mission
      const loadResult = yield* toGenerator(self._fetchMission(self.selectedMission.id))
      if (loadResult.error) {
        const error = { name: loadResult.error.name, message:`Failed to reload mission ${self.selectedMission.id}: ${loadResult.error.message}`}
        self._updateOperationStatus(self.saveMissionNoteStatuses, self.selectedMission.id, OperationFailure(error))
        return
      }
      // 'as unknown' an ugly trick, but it's really a SnapshotIn<typeof MissionHistoryModel>, not sure why mobx doesn't allow it
      applySnapshot(self.selectedMission, loadResult.result as unknown)
      self._updateOperationStatus(self.saveMissionNoteStatuses, self.selectedMission.id, OperationSuccess())

    })
  }))

  export type MissionNoteEditingClone = Instance<typeof MissionNoteEditingCloneModel>